import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

export function useAccordion(detailsRef: RefObject<HTMLDetailsElement>, duration = 400) {
    const stateRef = useRef<'idle' | 'expanding' | 'closing'>('idle');
    const summaryRef = useRef<HTMLElement>();
    const contentRef = useRef<HTMLDivElement>();
    const animationRef = useRef<Animation | null>(null);

    const animationConfig = useMemo(
        () => ({
            duration,
            easing: 'cubic-bezier(0.24, 0.1, 0.24, 1)',
        }),
        [duration],
    );

    const onFinish = useCallback<(open: boolean) => void>(
        (openState) => {
            if (!detailsRef.current) {
                return;
            }
            const el = detailsRef.current;

            el.open = openState;
            animationRef.current = null;
            stateRef.current = 'idle';
            el.style.height = el.style.overflow = '';
            delete el.dataset.animating;
        },
        [detailsRef],
    );

    const cancelAnimation = useCallback(() => {
        stateRef.current = 'idle';
    }, []);

    const completion = useMemo(
        () => ({
            open: onFinish.bind(null, true),
            close: onFinish.bind(null, false),
        }),
        [onFinish],
    );

    const expand = useCallback(() => {
        if (!detailsRef.current || !summaryRef.current || !contentRef.current) {
            return;
        }

        const el = detailsRef.current;
        const sum = summaryRef.current;
        const content = contentRef.current;

        stateRef.current = 'expanding';

        const styleEl = window.getComputedStyle(el);
        const padding = parseInt(styleEl.padding);
        const marginTop = parseInt(window.getComputedStyle(content).marginTop);
        const border = parseInt(styleEl.borderTopWidth) + parseInt(styleEl.borderBottomWidth);
        const startHeight = `${sum.offsetHeight + padding * 2 + border}px`;
        const endHeight = `${sum.offsetHeight + content.offsetHeight + marginTop + padding * 2 + border}px`;

        animationRef.current?.cancel();
        animationRef.current = el.animate(
            {
                height: [startHeight, endHeight],
            },
            animationConfig,
        );

        animationRef.current.onfinish = completion.open;
        animationRef.current.oncancel = cancelAnimation;
    }, [detailsRef, animationConfig, cancelAnimation, completion]);

    const open = useCallback(() => {
        if (!detailsRef.current) {
            return;
        }

        const el = detailsRef.current;

        el.style.height = `${el.offsetHeight}px`;

        el.open = true;
        window.requestAnimationFrame(expand);
    }, [detailsRef, expand]);

    const shrink = useCallback(() => {
        if (!detailsRef.current || !summaryRef.current || !contentRef.current) {
            return;
        }

        const el = detailsRef.current;
        const sum = summaryRef.current;
        const content = contentRef.current;

        stateRef.current = 'closing';

        const styleEl = window.getComputedStyle(el);
        const padding = parseInt(styleEl.padding);
        const marginTop = parseInt(window.getComputedStyle(content).marginTop);
        const border = parseInt(styleEl.borderTopWidth) + parseInt(styleEl.borderBottomWidth);
        const startHeight = `${sum.offsetHeight + content.offsetHeight + marginTop + padding * 2 + border}px`;
        const endHeight = `${sum.offsetHeight + padding * 2 + border}px`;

        animationRef.current?.cancel();
        animationRef.current = el.animate(
            {
                height: [startHeight, endHeight],
            },
            animationConfig,
        );
        animationRef.current.onfinish = completion.close;
        animationRef.current.oncancel = cancelAnimation;
    }, [detailsRef, animationConfig, cancelAnimation, completion]);

    const toggle = useCallback(() => {
        if (!detailsRef.current || stateRef.current !== 'idle') {
            return;
        }

        const el = detailsRef.current;

        if (!('animate' in el)) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            el.open = !el.open;
            return;
        }

        el.style.overflow = 'hidden';
        el.dataset.animating = '';

        if (el.open) {
            shrink();
        } else {
            open();
        }
    }, [detailsRef, open, shrink]);

    useEffect(() => {
        if (!detailsRef.current) {
            return;
        }

        const el = detailsRef.current;

        summaryRef.current = el.querySelector('summary') as HTMLElement;
        contentRef.current = el.querySelector('[data-content]') as HTMLDivElement;
    }, [detailsRef]);

    return { toggle, stateRef };
}
